<template>
  <button
    :class="classes"
    :data-test-id="dataTestId"
    :disabled="readonly || disabled"
    @click="emitClick"
  >
    <slot></slot>
  </button>
</template>

<script lang="ts">
interface ButtonProps {
  dataTestId?: string;
  variant?: Variant;
  size?: Size;
  fullwidth?: boolean;
  disabled?: boolean;
  readonly?: boolean;
}
</script>

<script lang="ts" setup>
import { computed, toRefs } from "vue";

import type { Variant, Size } from "./types";

const props = withDefaults(defineProps<ButtonProps>(), {
  dataTestId: "",
  variant: "default",
  size: "medium",
  fullwidth: false,
  disabled: false,
  readonly: false,
});

const { dataTestId, variant, size, fullwidth, disabled, readonly } =
  toRefs(props);

const emit = defineEmits<{
  (event: "on-click"): void;
}>();

const emitClick = (): void => {
  emit("on-click");
};

const mainStyles =
  "tw-m-1 tw-items-center tw-rounded tw-font-medium tw-outline-none tw-outline-offset-0 tw-transition-all focus:tw-outline-4";

const widthStyles = fullwidth.value
  ? "tw-flex tw-w-full tw-justify-center"
  : "tw-inline-flex";

const sizes = {
  small: "tw-text-sm tw-py-1 tw-px-2",
  medium: "tw-text-base tw-py-2 tw-px-6",
  large: "tw-text-lg tw-py-3 tw-px-10",
};

const variants = {
  default:
    "tw-text-dorian-gray-900 tw-bg-white hover:tw-bg-dorian-gray-50/20 active:tw-bg-dorian-gray-50/60 focus:tw-outline-dorian-gray-50",
  secondary:
    "tw-text-white tw-bg-secondary-500 hover:tw-bg-secondary-600 active:tw-bg-secondary-700 focus:tw-outline-secondary-500/30",
  "secondary-outline":
    "tw-text-secondary-500 tw-bg-transparent tw-border-0 tw-ring-2 tw-ring-inset tw-ring-secondary-500 enabled:hover:tw-bg-secondary-600 enabled:hover:tw-ring-secondary-600 enabled:hover:tw-text-white enabled:active:tw-bg-secondary-600 enabled:active:tw-text-white enabled:focus:tw-outline-secondary-500/30",
  "secondary-ghost":
    "tw-text-secondary-500 tw-bg-transparent tw-border-0 tw-ring-0 enabled:hover:tw-bg-dorian-gray-50 enabled:hover:tw-text-secondary-500 enabled:active:tw-bg-secondary-600 enabled:active:tw-text-white enabled:focus:tw-outline-secondary-500/30 ",
  info: "tw-text-white tw-bg-info-500 hover:tw-bg-info-600 active:tw-bg-info-700 focus:tw-outline-info-500/30 ",
  success:
    "tw-text-white tw-bg-success-500 hover:tw-bg-success-600 active:tw-bg-success-700 focus:tw-outline-success-500/30 ",
  warning:
    "tw-text-white tw-bg-warning-500 hover:tw-bg-warning-600 active:tw-bg-warning-700 focus:tw-outline-warning-500/30 ",
  danger:
    "tw-text-white tw-bg-danger-500 hover:tw-bg-danger-600 active:tw-bg-danger-700 focus:tw-outline-danger-500/30 ",
  "danger-outline":
    "tw-text-danger-500 tw-bg-transparent tw-border-0 tw-ring-2 tw-ring-inset tw-ring-danger-500 enabled:hover:tw-bg-danger-600 enabled:hover:tw-ring-danger-600 enabled:hover:tw-text-white enabled:active:tw-bg-danger-600 enabled:active:tw-text-white enabled:focus:tw-outline-danger-500/30",
  "link-secondary":
    "tw-bg-none tw-text-secondary-500 hover:tw-text-secondary-600 active:tw-text-secondary-800 focus:tw-outline-secondary-500/30",
  "i-am-the-danger":
    "tw-bg-none tw-text-danger-500 hover:tw-text-danger-600 active:tw-text-danger-800 focus:tw-outline-danger-500/30",
};

const disabledStyles = {
  default:
    "disabled:tw-bg-dorian-gray-200/60 disabled:tw-text-white dark:disabled:tw-text-dorian-gray-200",
  secondary:
    "disabled:tw-bg-dorian-gray-200/60 dark:disabled:tw-text-dorian-gray-200",
  "secondary-outline":
    "disabled:tw-ring-dorian-gray-200/60 disabled:tw-text-dorian-gray-200/60 disabled:tw-bg-transparent",
  "secondary-ghost": "disabled:tw-text-dorian-gray-200/60",
  info: "disabled:tw-bg-dorian-gray-200/60 dark:disabled:tw-text-dorian-gray-200",
  success:
    "disabled:tw-bg-dorian-gray-200/60 dark:disabled:tw-text-dorian-gray-200",
  warning:
    "disabled:tw-bg-dorian-gray-200/60 dark:disabled:tw-text-dorian-gray-200",
  danger:
    "disabled:tw-bg-dorian-gray-200/60 dark:disabled:tw-text-dorian-gray-200",
  "danger-outline":
    "disabled:tw-ring-dorian-gray-200/60 disabled:tw-text-dorian-gray-200/60 disabled:tw-bg-transparent",
  "link-secondary": "disabled:tw-text-dorian-gray-200/60",
  "i-am-the-danger": "disabled:tw-text-dorian-gray-200/60",
};

const readonlyStyles = {
  default:
    "disabled:tw-bg-dorian-gray-200/40 disabled:tw-text-white dark:disabled:tw-text-dorian-gray-200",
  secondary: "disabled:tw-text-white disabled:tw-bg-secondary-200",
  "secondary-outline":
    "disabled:tw-text-secondary-300 disabled:tw-ring-secondary-200",
  "secondary-ghost": "disabled:tw-text-secondary-300",
  info: "disabled:tw-text-white disabled:tw-bg-info-200",
  success: "disabled:tw-text-white disabled:tw-bg-success-200",
  warning: "disabled:tw-text-white disabled:tw-bg-warning-200",
  danger: "disabled:w-text-white disabled:tw-bg-danger-200 ",
  "danger-outline":
    "disabled:tw-text-danger-200 disabled:tw-bg-transparent disabled:tw-ring-danger-200",
  "link-secondary": "disabled:tw-bg-none disabled:tw-text-secondary-200",
  "i-am-the-danger": "disabled:tw-bg-none disabled:tw-text-danger-200",
};

const classes = computed(() => {
  return [
    mainStyles,
    widthStyles,
    sizes[size.value],
    variants[variant.value],
    readonly.value
      ? readonlyStyles[variant.value]
      : disabledStyles[variant.value],
    readonly.value && "disabled:tw-cursor-not-allowed",
    disabled.value && "disabled:tw-cursor-default",
  ];
});
</script>

<style scoped></style>
